import React from 'react';
import { useOrion } from '../context/OrionContext';
import { VIEW_META } from './Topbar';
import Icon from './Icon';
import LogoOrion from './LogoOrion';

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar — a navegação.
//
// As telas são as MESMAS quatro do Orion 1.4.1: Formalização (Helpdesk),
// Incidentes, Chamado T.I. e Histórico. Nada além disso — a aba de busca na
// base de conhecimento foi retirada da versão 1.x e não volta aqui: o lugar
// dela é o Zarion, no rodapé, que abre o site.
//
// `aoNavegar` só faz diferença na janela estreita: escolher um item precisa
// fechar a barra, senão a pessoa toca em "Incidentes" e continua olhando para
// o menu, com a tela que pediu atrás dele.
// ─────────────────────────────────────────────────────────────────────────────

// A lista completa. O que cada operador vê depende do setor dele — quem
// decide é services/permissoes.js, e o filtro acontece na hora de montar o
// menu. Um item sem permissão não fica desabilitado: ele não existe.
//
// "Formalização rápida" aparece SÓ para quem não é do suporte. Para o suporte
// ela continua sendo o atalho dentro da própria Formalização, onde ela nasceu:
// duas entradas para a mesma ferramenta, na mesma barra, seria confuso.
// "Filas do setor" fica em Atendimento, e não num grupo de gerência: ela é de
// TODO o suporte, e serve para pegar trabalho — é atendimento, não coordenação.
// Quem é gerente vê os mesmos itens; o que muda para ele são blocos DENTRO da
// tela (ver services/permissoes.js, chave `gerenciaEquipe`).
//
// Vem logo depois da Formalização porque é o segundo lugar mais visitado do
// dia: termina um atendimento, volta aqui para escolher o próximo.
const ITENS = [
  { grupo: 'Atendimento' },
  // "Minha Área" e não "Formalização": desde que as filas do setor entraram ao
  // lado, o par ficou sendo o MEU trabalho × o do setor, e é assim que a pessoa
  // pensa a escolha. O nome interno da view segue `helpdesk`.
  { chave: 'helpdesk', label: 'Minha Área' },
  { chave: 'gerencia', label: 'Filas do setor' },
  // Consultar por número fica no ATENDIMENTO, logo abaixo das filas: é a
  // terceira maneira de chegar a um ticket, ao lado do que é meu e do que é
  // do setor — e a única que alcança o ticket que não está em lista nenhuma.
  { chave: 'busca-ticket', label: 'Consultar ticket' },
  { chave: 'formalizacao-rapida', label: 'Formalização rápida', soSemSuporte: true },
  { chave: 'incidentes', label: 'Incidentes' },
  { grupo: 'Apoio' },
  { chave: 'chamado-ti', label: 'Chamado T.I.' },
  { chave: 'atividade', label: 'Atividade' },
  { chave: 'historico', label: 'Histórico' },
];

/**
 * Tira os itens que este operador não pode abrir — e os rótulos de grupo que
 * ficaram sem nenhum item embaixo, que de outro modo apareceriam sozinhos.
 */
function itensVisiveis(permissoes) {
  const permitidos = ITENS.filter((it) => {
    if (it.grupo) return true;
    if (it.soSemSuporte && permissoes.suporte) return false;
    return permissoes[it.chave] !== false;
  });

  return permitidos.filter((it, i) => {
    if (!it.grupo) return true;
    const proximo = permitidos[i + 1];
    return !!proximo && !proximo.grupo;
  });
}

/** Iniciais do operador para o avatar: primeiro e último nome. */
function iniciaisDe(nome) {
  const partes = String(nome || '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

function Sidebar({ aoNavegar }) {
  const {
    operador, view, setView, sidebarRecolhida, setSidebarRecolhida, sair, ehDesktop,
    permissoes, notificacoes,
  } = useOrion();
  const naoLidas = notificacoes?.naoLidas || 0;

  const itens = itensVisiveis(permissoes);

  const irPara = (chave) => {
    setView(chave);
    if (aoNavegar) aoNavegar();
  };

  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-logo"><LogoOrion size={22} /></div>
        <div className="brand-name">Orion</div>
        {/* O Orion deixou de ser só do suporte: a tag mostra o setor de quem
            está usando, e não mais um rótulo fixo. */}
        <span className="brand-tag">
          {String(operador?.setor || '').trim().toUpperCase() || 'SPACE'}
        </span>
        <button
          className="side-collapse"
          onClick={() => setSidebarRecolhida(v => !v)}
          title={sidebarRecolhida ? 'Expandir menu' : 'Recolher menu'}
          aria-label={sidebarRecolhida ? 'Expandir menu' : 'Recolher menu'}
        >
          <Icon name={sidebarRecolhida ? 'chevron-right' : 'chevron-left'} size={15} />
        </button>
      </div>

      <nav>
        {itens.map((it, i) => {
          if (it.grupo) return <div className="nav-label" key={`g-${i}`}>{it.grupo}</div>;
          const icone = VIEW_META[it.chave]?.icon || 'grid';
          return (
            <button
              key={it.chave}
              className={`nav-item ${view === it.chave ? 'active' : ''}`}
              onClick={() => irPara(it.chave)}
              title={it.label}   /* com o menu recolhido só resta o ícone */
            >
              <span className="ni-ic"><Icon name={icone} size={15} /></span>
              {it.label}
            </button>
          );
        })}
      </nav>

      <div className="side-foot">
        {/* Notificações: a mesma caixa de entrada do sininho no topo. */}
        <button
          className={`nav-item nav-item-sutil ${view === 'notificacoes' ? 'active' : ''}`}
          onClick={() => irPara('notificacoes')}
          title="Notificações"
        >
          <span className="ni-ic"><Icon name="bell" size={15} /></span>
          Notificações
          {naoLidas > 0 && <span className="side-ntf-cnt">{naoLidas > 99 ? '99+' : naoLidas}</span>}
        </button>

        {/* Zarion: base de conhecimento da Space. Abre DENTRO do app, como
            mais uma tela — o Orion não reimplementa a busca, que continua
            sendo mantida lá. Fica fora da navegação principal porque é
            consulta, não atendimento. */}
        <button
          className={`nav-item nav-item-sutil ${view === 'zarion' ? 'active' : ''}`}
          onClick={() => irPara('zarion')}
          title="Abrir o Zarion (base de conhecimento)"
        >
          <span className="ni-ic"><Icon name="compass" size={15} /></span>
          Zarion
        </button>

        <button
          className={`nav-item nav-item-sutil ${view === 'configuracoes' ? 'active' : ''}`}
          onClick={() => irPara('configuracoes')}
          title="Configurações"
        >
          <span className="ni-ic"><Icon name="settings" size={15} /></span>
          Configurações
        </button>

        {/* Notas de versão: texto sobre o aplicativo, não atendimento — por
            isso no rodapé, junto de Configurações. O número da versão no topo
            leva ao mesmo lugar. */}
        <button
          className={`nav-item nav-item-sutil ${view === 'notas' ? 'active' : ''}`}
          onClick={() => irPara('notas')}
          title="Notas de versão"
        >
          <span className="ni-ic"><Icon name="release-notes" size={15} /></span>
          Notas de versão
        </button>

        {/* Onde o Orion está rodando agora. O mesmo código serve os dois. */}
        <div className="platform">
          <span><Icon name={ehDesktop ? 'monitor' : 'globe'} size={12} /> {ehDesktop ? 'Desktop' : 'Web'}</span>
        </div>

        <div className="user-card">
          <div className="avatar">{iniciaisDe(operador?.nome)}</div>
          <div>
            {/* o nome corta com reticências quando é comprido; o title mostra inteiro */}
            <div className="user-name" title={operador?.nome}>{operador?.nome}</div>
            <div className="user-role" title={operador?.email}>
              {operador?.codigo ? `Operador ${operador.codigo}` : 'Sem cadastro no Orion'}
            </div>
          </div>
          <button className="jn-btn" onClick={sair} title="Trocar operador">
            <Icon name="log-out" size={15} />
          </button>
        </div>
      </div>
    </aside>
  );
}

// A barra só muda quando muda a tela ativa, o operador ou o estado de recolhida
// — memo evita que ela seja refeita a cada digitação nas telas de formulário.
export default React.memo(Sidebar);
